import {BaseApi} from './BaseApi'
import {Site} from './siteApi'
import {sanitizePayload} from '../utils/apiUtils'

// Enums matching MSP NodeJS service
export enum IndustryType {
  Retail = 'Retail',
  // Add more as needed
}

export enum CompanyType {
  SoleProprietorship = 'Sole Proprietorship',
  GeneralPartnership = 'General Partnership',
  LimitedPartnership = 'Limited Partnership (LP)',
  LimitedLiabilityPartnership = 'Limited Liability Partnership (LLP)',
  LimitedLiabilityCompany = 'Limited Liability Company (LLC)',
  CCorporation = 'C Corporation',
  SCorporation = 'S Corporation',
  BenefitCorporation = 'Benefit Corporation',
  NonprofitCorporation = 'Nonprofit Corporation',
  Cooperative = 'Cooperative (Co-op)',
  JointVenture = 'Joint Venture (JV)',
}

export enum CompanyPayRollFrequency {
  Weekly = 'Weekly',
  BiWeekly = 'Bi-weekly',
  Monthly = 'Monthly',
  SemiMonthly = 'Semi-monthly',
}

export enum EntityStatus {
  Pending = 'Pending',
  Active = 'Active',
  Disabled = 'Disabled',
  Creating = 'Creating',
  Failed = 'Failed',
  Archived = 'Archived',
}

// Interfaces matching MSP NodeJS service
export interface Address {
  line1: string
  city: string
  state: string
  zipCode: string
  display?: string
}

export interface Region {
  id: string
  name: string
  description?: string
}

export interface Company {
  id: string
  name: string
  phoneNumber: string
  phoneExtension?: string
  adminFirstName: string
  adminLastName: string
  adminEmail: string
  companyAddress: Address
  billingAddress: Address
  agencyId: string
  companyWebsite?: string
  industry: IndustryType
  companyType: CompanyType
  paySchedule: CompanyPayRollFrequency // Updated from payRoll
  taxId: string
  standardPayDaylag: number // Updated from paymentRollOutAfter
  payPeriodStartDay?: Date // Updated from startingDay
  payPeriodEndDay?: Date // Updated from endingDay
  status?: EntityStatus
  regions?: Region[]
  sites?: Site[]
  createdDate?: string
  modifiedDate?: string
}

// Request DTOs
export interface CreateCompanyRequest {
  name: string
  phoneNumber: string
  phoneExtension?: string
  adminFirstName: string
  adminLastName: string
  adminEmail: string
  companyAddress: Address
  billingAddress: Address
  agencyId: string
  companyWebsite?: string
  industry: IndustryType
  companyType: CompanyType
  paySchedule: CompanyPayRollFrequency // Updated from payRoll
  taxId: string
  standardPayDaylag: number // Updated from paymentRollOutAfter
  payPeriodStartDay: string // Updated from startingDay
  payPeriodEndDay: string // Updated from endingDay
}

export interface CreateRegionRequest {
  name: string
  description?: string
}

export interface UpdateRegionRequest {
  name?: string
  description?: string
}

export interface UpdateCompanyRequest {
  id: string
  name?: string
  phoneNumber?: string
  phoneExtension?: string
  adminFirstName?: string
  adminLastName?: string
  adminEmail?: string
  companyAddress?: Address
  billingAddress?: Address
  companyWebsite?: string
  industry?: IndustryType
  companyType?: CompanyType
  paySchedule?: CompanyPayRollFrequency // Updated from payRoll
  taxId?: string
  standardPayDaylag?: number // Updated from paymentRollOutAfter
  payPeriodStartDay?: string // Updated from startingDay
  payPeriodEndDay?: string // Updated from endingDay
  agencyId?: string // Agency assignment
  payRollStartDate?: Date
}

export interface CompanyPagingModel {
  companies: Company[]
  totalCompanies: number
  currentPage: number
  totalPages: number
}

export interface CompanyNamesAndIds {
  id: string
  name: string
}

export interface RegionHierarchyResponse {
  id: string
  name: string
  sites: Site[]
}

export interface CompanyHierarchyResponse {
  id: string
  name: string
  numSites: number
  companySites: Site[]
  regions: RegionHierarchyResponse[]
  // Payroll configuration fields
  paySchedule?: string
  payPeriodStartDay?: string
  payPeriodEndDay?: string
  payRollStartDate?: string // ISO date string
  standardPayDaylag?: number
  // Agency assignment
  agencyId?: string
  agencyName?: string
  // EOR information for paycode lookups
  eor?: {
    id: string
    name: string
  }
}

export interface PagingParametersDto {
  pageNumber?: number
  pageSize?: number
  search?: string
}

export interface VerifyIfAgencyManageCompanyRequest {
  companyId: string
  agencyIds: string[]
}

export interface VerifyIfAgencyManageCompanyResponse {
  canManage: boolean
  managingAgencyId?: string
}

export interface GetCompaniesQueryDto extends PagingParametersDto {
  agencyId?: string
  status?: EntityStatus
  adminEmail?: string
  createdDate?: string // ISO date string
  name?: string
}

/**
 * Company API service for MSP NodeJS endpoints
 * Handles company CRUD operations, regions, and hierarchy
 */
export class CompanyApi extends BaseApi {
  /**
   * Get company name by ID
   * GET /api/company/getCompanyNameById/:id
   */
  async getCompanyNameById(id: string): Promise<string> {
    try {
      const response = await this.client.get<string>(`/api/company/getCompanyNameById/${id}`)
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching company name:', id, error)
      throw error
    }
  }

  /**
   * Get paginated companies with enhanced filtering
   * GET /api/company/getCompanies
   */
  async getCompanies(query?: GetCompaniesQueryDto): Promise<CompanyPagingModel> {
    try {
      const response = await this.client.get<CompanyPagingModel>('/api/company/getCompanies', {
        params: query,
      })
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching companies:', error)
      throw error
    }
  }

  /**
   * Get all companies
   * GET /api/company/getAllCompanies
   * @param agencyId - Optional agency ID to filter companies (required for GA users)
   */
  async getAllCompanies(agencyId?: string): Promise<Company[]> {
    try {
      const response = await this.client.get<Company[]>('/api/company/getAllCompanies', {
        params: agencyId ? {agencyId} : undefined,
      })
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching all companies:', error)
      throw error
    }
  }

  /**
   * Verify if agency can manage company
   * POST /api/company/verifyIfAgencyManageCompany
   */
  async verifyIfAgencyManageCompany(
    request: VerifyIfAgencyManageCompanyRequest,
  ): Promise<VerifyIfAgencyManageCompanyResponse> {
    try {
      const response = await this.client.post<VerifyIfAgencyManageCompanyResponse>(
        '/api/company/verifyIfAgencyManageCompany',
        request,
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error verifying agency-company relationship:', error)
      throw error
    }
  }

  /**
   * Get company names and IDs
   * GET /api/company/getAllCompanyNamesAndIds
   * @param pagingParams - Optional paging parameters
   * @param agencyId - Optional agency ID to filter companies (required for GA users)
   */
  async getAllCompanyNamesAndIds(
    pagingParams?: PagingParametersDto,
    agencyId?: string,
  ): Promise<CompanyNamesAndIds[]> {
    try {
      const response = await this.client.get<CompanyNamesAndIds[]>(
        '/api/company/getAllCompanyNamesAndIds',
        {
          params: {...pagingParams, agencyId},
        },
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching company names and IDs:', error)
      throw error
    }
  }

  /**
   * Get company by ID
   * GET /api/company/getCompany/:id
   */
  async getCompanyById(id: string): Promise<Company | null> {
    try {
      const response = await this.client.get<Company | null>(`/api/company/getCompany/${id}`)
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching company:', id, error)
      throw error
    }
  }

  /**
   * Get company hierarchy
   * GET /api/company/getCompanyHierarchy/:id
   */
  async getCompanyHierarchy(id: string): Promise<CompanyHierarchyResponse> {
    try {
      const response = await this.client.get<CompanyHierarchyResponse>(
        `/api/company/getCompanyHierarchy/${id}`,
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching company hierarchy:', id, error)
      throw error
    }
  }

  /**
   * Create new company
   * POST /api/company/onboardCompany
   */
  async createCompany(companyData: CreateCompanyRequest): Promise<Company> {
    try {
      const sanitizedData = sanitizePayload(companyData)
      const response = await this.client.post<Company>('/api/company/onboardCompany', sanitizedData)
      return response.data
    } catch (error: unknown) {
      console.error('Error creating company:', companyData.name, error)
      throw error
    }
  }

  /**
   * Update company
   * PUT /api/company/updateCompany
   */
  async updateCompany(companyData: UpdateCompanyRequest): Promise<Company> {
    try {
      const sanitizedData = sanitizePayload(companyData)
      const response = await this.client.put<Company>('/api/company/updateCompany', sanitizedData)
      return response.data
    } catch (error: unknown) {
      console.error('Error updating company:', companyData.id, error)
      throw error
    }
  }

  /**
   * Add region to company
   * PUT /api/company/addCompanyRegion
   */
  async addCompanyRegion(companyId: string, regionData: CreateRegionRequest): Promise<Company> {
    try {
      const sanitizedData = sanitizePayload(regionData)
      const response = await this.client.put<Company>(
        `/api/company/addCompanyRegion?companyId=${companyId}`,
        sanitizedData,
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error adding region to company:', companyId, error)
      throw error
    }
  }

  /**
   * Update company region
   * PUT /api/company/updateCompanyRegion
   */
  async updateCompanyRegion(
    companyId: string,
    regionId: string,
    regionData: UpdateRegionRequest,
  ): Promise<Company> {
    try {
      const sanitizedData = sanitizePayload(regionData)
      const response = await this.client.put<Company>(
        `/api/company/updateCompanyRegion?companyId=${companyId}&regionId=${regionId}`,
        sanitizedData,
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error updating company region:', regionId, error)
      throw error
    }
  }

  /**
   * Update company status (archive/activate)
   * PUT /api/company/updateCompanyStatus/:id/Status/:status
   */
  async updateCompanyStatus(id: string, status: EntityStatus): Promise<Company> {
    try {
      const response = await this.client.put<Company>(
        `/api/company/updateCompanyStatus/${id}/Status/${status}`,
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error updating company status:', id, status, error)
      throw error
    }
  }

  /**
   * Delete company
   * DELETE /api/company/deleteCompany
   */
  async deleteCompany(id: string): Promise<Company> {
    try {
      const response = await this.client.delete<Company>(`/api/company/deleteCompany?id=${id}`)
      return response.data
    } catch (error: unknown) {
      console.error('Error deleting company:', id, error)
      throw error
    }
  }

  /**
   * Get company by name
   * GET /api/company/getCompanyByName/:name
   */
  async getCompanyByName(name: string): Promise<Company> {
    try {
      const response = await this.client.get<Company>(`/api/company/getCompanyByName/${name}`)
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching company by name:', name, error)
      throw error
    }
  }

  /**
   * Get companies by industry type
   * GET /api/company/getCompaniesByIndustry/:industryType
   */
  async getCompaniesByIndustry(industryType: IndustryType): Promise<Company[]> {
    try {
      const response = await this.client.get<Company[]>(
        `/api/company/getCompaniesByIndustry/${industryType}`,
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching companies by industry:', industryType, error)
      throw error
    }
  }

  /**
   * Get companies by status
   * GET /api/company/getCompaniesByStatus/:status
   */
  async getCompaniesByStatus(status: EntityStatus): Promise<Company[]> {
    try {
      const response = await this.client.get<Company[]>(
        `/api/company/getCompaniesByStatus/${status}`,
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching companies by status:', status, error)
      throw error
    }
  }

  /**
   * Archive company
   * PUT /api/company/archiveCompany/:id
   */
  async archiveCompany(id: string): Promise<Company> {
    try {
      const response = await this.client.put<Company>(
        `/api/company/updateCompanyStatus/${id}/Status/${EntityStatus.Archived}`,
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error archiving company:', id, error)
      throw error
    }
  }

  /**
   * Restore archived company
   * PUT /api/company/restoreCompany/:id
   */
  async restoreCompany(id: string): Promise<Company> {
    try {
      const response = await this.client.put<Company>(
        `/api/company/updateCompanyStatus/${id}/Status/${EntityStatus.Active}`,
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error restoring company:', id, error)
      throw error
    }
  }

  /**
   * Get company names that have placed orders (for filter dropdowns)
   * GET /api/company/getCompanyNamesWithOrders
   */
  async getCompanyNamesWithOrders(): Promise<CompanyNamesAndIds[]> {
    try {
      const response = await this.client.get<CompanyNamesAndIds[]>(
        '/api/company/getCompanyNamesWithOrders',
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching company names with orders:', error)
      throw error
    }
  }
}

export const companyApi = new CompanyApi()
